/**
 * Base Color Palette
 * 
 * This file defines all base color values used throughout the application.
 * Import and reference these colors in other color group files to maintain consistency
 * and enable easy theme-wide color updates.
 * 
 * Usage:
 * import { palette } from './palette.colors';
 * 
 * export const myColors = {
 *   primary: { light: palette.blue.primary, dark: palette.blue.primary }
 * };
 */

export const palette = {
  // Blues
  blue: {
    primary: '#2B78CA',      // Primary blue used throughout app
    secondary: '#1A65B8',    // WCAG 1.4.3: was #2C7CD3 (3.6:1 vs white text) → #1A65B8 (5.1:1)
    light: '#66AFDA',        // Light blue
    appBG: '#F5F9FF',        // App-wide page background (light blue tint)
    selectedBorder: '#66ABFF', // Selected/active border blue
    selectedItemBG: '#E6F1FF', // Selected item background in dropdown
    selectedText: '#005FCC',   // Selected item text color in dropdown / brand CTA primary
    hover: '#004EAB',        // Brand CTA hover fill
    pressed: '#002859',      // Brand CTA pressed / deepest fill
    badge: '#004EA8',        // Badge / counter background fill
    onTint: '#020617',       // Text & icons drawn ON light tint fills (dark navy)
    medium: '#3B6FA8',       // Medium blue
    dark: '#1E4A7A',         // Dark blue
    darkest: '#00234D',      // Darkest blue
    pale: '#D9EBF6',         // Pale blue
    tint: '#81B7DE1F',       // Blue tint with opacity
    sky: '#EAF1FA',          // Sky blue
    icon: '#1447E6',
    unReadBlue: '#F2F7FC',
    paleSecondary: '#DEE9F1',
    soft: '#99C7FF',         // Soft/pastel blue — between tintMed and border
  },

  // Grays
  gray: {
    white: '#FFFFFF',
    almostWhite: '#F6F6F6',
    lightWhite: '#F1F2F3',
    veryLight: '#F9FAFB',
    ultraLight: '#FAFAFA',
    light: '#F0F0F0',
    extraLight: '#F3F4F6',
    border: '#D0D0D0',
    mainBorder: '#D0D0D0',
    medium: '#D0D5DD',
    neutral: '#D0D0D0',
    disabledNeutral: '#D0D0D04D',
    dark: '#707070',
    darker: '#3F3F3F',
    darkest: '#1C1C1C',
    black: '#000000',
    offBlack: '#09090B',
    almostBlack: '#0A0A0A',
    shade: '#141414',
    card: '#111113',
    deep: '#1A1A1A',
    separator: '#E4E4E7',
    divider: '#E5E7EB',
    icon: '#6B7280',         // WCAG 1.4.3: was #7A7A7A (4.48:1 vs white) → #6B7280 (4.6:1)
    text: '#6B7280',         // WCAG 1.4.3: was #9A9A9A (2.85:1 vs white) → #6B7280 (4.6:1)
    accessibleMid: '#595959',   // WCAG 1.4.3: 7.0:1 vs white – used for secondary text/icons in profile dropdown
    subText: '#707070',
    disabled: '#CBD5E1',
    slate: '#64748B',        // WCAG 1.4.3: was #94A3B8 (2.8:1 vs white) → #64748B (4.6:1)
    zinc: '#6B7280',         // WCAG 1.4.3: was #7C7C7C (4.48:1 vs white) → #6B7280 (4.6:1)
    // Additional gray shades
    veryDark: '#212529',
    mediumShade: '#495057',
    lightBorder2: '#DEE2E6',
    backgroundLight: '#F8F9FA',
    iconLight: '#E9ECEF',
    iconBG: '#EDEDED',
    borderAlt2: '#D1D5DC',
    textDark: '#333333',
    charcoal: '#4B5563',
    smoke: '#929292',
    borderLight: '#E5E5E5',
    borderMedium: '#E0E0E0',
    avatar: '#99A1AF',
    mediumDark: '#4A5565',
    textDarker: '#364153',
    textSubtle: '#6A7282',
    techInfo: '#F8F8F8',
    security: '#F7F9FC',
    field: '#101828',
    fieldAlt: '#111827',
    dots: '#454A54',
    userInitial: '#888',
    pureDark: '#00000000',
    additionalBadge: '#DFDEDE',
    charCountGrey: '#F8F6F6',
    separatorGreyBorder: '#DFDFDFB2',
    bottomGreyBorder: '#DFDFDF',
    lowLightGrey: '#B1B0B0',
    tabDisabled: '#CED8E3',
    lightDarkGrey: '#26364A',
    disabledBG: '#E2E8F0',
    slateLight: '#F8FAFC',
    darkGreyLight: '#3A4B61',
    disabledCTABG: '#B6C2CF',
    disabledCTAText: '#3A4B61',
    tabDisabledText: '#A1B4C3',
    placeholderText: '#667891',
    searchResult: '#4F6178'
  },

  // Semantic Colors
  semantic: {
    red: '#E53935',
    darkRed: '#B7131F',
    dangerRed: '#C92E37',
    errorRed: '#E7000B',
    highPriorityRed: '#DC000B',
    outOfStockRed: '#C10007',
    green: '#008000',
    brightGreen: '#00E304',
    mediumGreen: '#009B40',
    darkGreen: '#008236',
    issuedGreen: '#007D0F',
    lowPriorityGreen: '#008A11',
    // Figma design-token success scale
    success200: '#C8F0E1',   // success/200 — success toast background
    success500: '#10B981',   // success/500 — success toast border & icon
    // Figma design-token error-secondary scale
    errorSecondary500: '#EB1A28', // error-secondary/500 — same as dangerRed; alias for clarity
    orange: '#FE9A00',
    mediumOrange: '#CE8200',
    lightOrange: '#BA5C00',
    amberOrange: '#B26000',
    warningOrange: '#B55A00',
    warningDark: '#7B3306',
    warningMedium: '#973C00',
    readyBlue: '#0C6ED0',
    onOrderPurple: '#A65EFF',
  },

  // Status Colors
  status: {
    offline: {
      bg: '#ECECF0',
      text: '#717182',
    },
    online: {
      bg: '#E8FFF0',
      text: '#008236',
    },
  },

  // Background Colors
  backgrounds: {
    lightOnOrder: '#F1E6FF',
    lightRed: '#FEF2F2',
    pink: '#FDEAEC',
    errorSecondary200: '#FACBCF', // error-secondary/200 — error toast background (Figma)
    lightestRed: '#FFF7F7',
    successGreen: '#F0FDF4',
    lightBlue: '#EFF6FF',
    lightYellow: '#FFFEF1',
    requestedBlue: '#ECF4FE',
    warningYellow: '#FFFCD2',
    white: '#F1F5F9',
    offWhite: '#0F172A',
    lightWhite: '#F9FAFBB2',
    successBG: '#E4F1D5',
    /** App-level page background tints for non-blue brand themes */
    redAppBG: '#FFF5F5',       // Lightest red tint — red brand canvas
    greenAppBG: '#F5FFF6',     // Lightest green tint — green brand canvas
    purpleAppBG: '#F9F5FF',    // Lightest purple tint — purple brand canvas
    orangeAppBG: '#FFF8F2',    // Lightest orange tint — orange brand canvas
    tealAppBG: '#F0FAF9',      // Lightest teal tint — teal brand canvas
    slateAppBG: '#F8FAFC',     // Lightest slate tint — slate brand canvas
  },

  // Border Colors
  borders: {
    selected: '#B1CDEC',
    blue: '#BEDBFF',
    dropdownBlue: '#CCE3FF',  // Dropdown border (all states)
    yellow: '#F2D1B1',
    tech: '#2E303E',
  },

  // Specific UI Colors
  ui: {
    linkBlue: '#007BFF',
    hoverBlue: '#0079C2',
    activeBlue: '#155DFC',
    activityBlue: '#007AFF',
    disabled: '#AFAFAF',
    shadow: '#1D38661A',
  },

  // Opacity Variants
  opacity: {
    black40: '#00000040',           // Dropdown shadow (25% opacity)
    black05: 'rgba(0, 0, 0, 0.05)',
    black0D: '#0000000D',
    black10: 'rgba(0, 0, 0, 0.1)',
    black13: '#0000001A',
    black55: 'rgba(0, 0, 0, 0.55)',
    black60: 'rgba(0, 0, 0, 0.6)',
    black20: 'rgba(0, 0, 0, 0.2)',
    white55: 'rgba(255, 255, 255, 0.55)',
    white80: 'rgba(255, 255, 255, 0.30)',
    darkest1A: '#1C1C1C1A',
    white06: 'rgba(255, 255, 255, 0.06)',
    white08: 'rgba(255, 255, 255, 0.08)',
    white20: '#FFFFFF33',
    white70: '#FFFFFFB2',
    whiteB2: '#F6F6F6B2',
    gray50: '#D0D0D080',
    gray80: '#DFDFDF80',
    gray80alt: '#E5E5E580',
    gray86: '#D9D9D980',
    grayAlpha: '#DFDFDF1A',
    veryLight50: 'rgba(249, 250, 251, 0.50)',
    blue13: '#2B78CA1A',
    blue08: '#2B78CA0D',
    red13: '#CD0C0C1A',
    mediumYellow: '#FFFCD233',
    red33: '#FF666633',
    highPriority: '#FFE4E5',
    highPriorityBorder: '#CD0C0C',
    mediumPriority: '#FFF3DE',
    mediumPriorityBorder: '#A56A03',
    lowPriority: '#F0F7FF',
    lowPriorityBorder: '#0C6ED0',
    activeStatus: 'rgba(43, 127, 255, 0.05)',
    activeStatusBorder: 'rgba(43, 127, 255, 0.20)',
    completedStatus: 'rgba(255, 252, 210, 0.20)',
    completedStatusBorder: 'rgba(118, 204, 99, 0.20)',
    assignedStatus: 'rgba(255, 252, 210, 0.20)',
    assignedStatusBorder: 'rgba(255, 170, 24, 0.20)',
    overlayDark: 'rgba(0, 0, 0, 0.5)',
    modalBackground: 'rgba(0, 0, 0, 0.6)',
    shadowBlue: 'rgba(29, 56, 102, 0.24)',
    modalBackdrop: 'rgba(0, 0, 0, 0.6)',
    leastPrioriryBG: '#F1FFF3',
    leastPrioriryBorder: '#008A11',
    grey20: '#92929233',
    green20Background: '#76CC6333',
    primaryBlueLight: '#2B78CA1F',
    novaVoiceBase: 'rgba(255,255,255,0.9)'
  },

  // Special Colors
  special: {
    transparent: 'transparent',
    shadow: '#000000',
    shadowLight: 'rgba(65, 74, 83, 0.2)',
  },

  // Dark Mode Specific
  dark: {
    background: '#181920',
    content: '#1E1F27',
    border: '#262626',
    borderAlt: '#27272A',
    borderLight: '#323232',
    card: '#191919',
    cardAlt: '#242424',
    cardSelected: '#292929',
    cardDark: '#2A2A2A',
    input: 'rgba(39, 39, 39, 1)',
    text: '#B0BEC5',         // WCAG 1.4.3: was #A8B7C3 (4.4:1 vs #181920) → #B0BEC5 (4.6:1)
    textAlt: '#9DB0C0',      // WCAG 1.4.3: was #8496AB (3.5:1 vs #181920) → #9DB0C0 (4.7:1)
    textLight: '#BEBEBE',
    surface: '#252630',
    overlay: '#343434',
    techBorder: '#2E303E',
    menu: '#272727',
    statusDate: '#303030',
  },
};

/**
 * Helper function to create color objects with light/dark variants
 */
export const createColor = (light: string, dark?: string) => ({ 
  light, 
  dark: light || light 
  // dark: dark || light 
});